/* @flow */

import * as React from 'react';
import Select, {
    components,
    type OptionProps,
    type MultiValueProps,
    type ValueContainerProps,
} from 'react-select';
import classnames from 'classnames';

import {Button} from 'shells/button';
import {Avatar} from '../avatar';
import {EntityPillSelectWrapper} from '../entity-pill';
import {selectPickerBaseStyles} from './select-picker-base-styles';

import styles from './select-picker.css';

/*
 * This component is a simple wrapper around the react-select
 * component and primarily uses its API.
 * https://github.com/JedWatson/react-select
 */

type Props = {
    name: string,
    label: string,
    component: any,
    valueKey: string,
    labelKey: string,

    // Purposefully loosely typed, this breaks everything otherwise
    // This component is always rendered by a stricter typed parent
    //
    // Technically, with multi it is:
    // `onChange: (selectedOptions: Object[]) => any`
    //
    // and non-multi:
    // `onChange: (selectedOption: ?Object`) => any`
    onChange: Function,

    // Similarly with onChange, this value can either be one of two
    // types, depending on the `multi` prop.
    //
    // If multi is true:
    // `value: Object[]`
    //
    // and non-multi:
    // `value: ?Object`
    value: any,

    // This signifies an Async, multi-select
    loadOptions?: (query: string) => Promise<Object[]>,

    // Signifies a multi
    multi?: boolean,
    truncateMultiOptions?: boolean,

    // These are only required if loadOptions is not present
    options?: Object[],

    // Creatable options
    formatCreateLabel?: (string) => any,
    getNewOptionData?: (string, React.Node) => any,
    onCreateOption?: Function,

    getRef?: (?Select) => any,
    autoBlur?: boolean,
    clearable?: boolean,
    disabled?: boolean,
};

type State = {
    hasFocus: boolean,
};

export class SelectPickerBase extends React.PureComponent<Props, State> {
    containerRef: ?HTMLElement;

    static defaultProps = {
        autoBlur: false,
    };

    constructor() {
        super();
        this.state = {
            hasFocus: false,
        };
    }

    render() {
        const hasValue = this.props.multi
            ? Boolean(this.props.value.length)
            : Boolean(this.props.value);

        const SelectComponent = this.props.component;
        const labelStyleName = classnames('styles.label', {
            'styles.label--active': hasValue || this.state.hasFocus,
        });

        const {labelKey, valueKey} = this.props;

        return (
            <div
                styleName='styles.container'
                ref={(node) => {
                    this.containerRef = node;
                }}
            >
                <div styleName={labelStyleName}>{this.props.label}</div>
                <SelectComponent
                    // Sane defaults
                    placeholder=''
                    openMenuOnFocus={true}
                    isValid={true}
                    tabSelectsValue={false}
                    defaultOptions={true}
                    ref={this.props.getRef}
                    // Styling
                    className={styles.select}
                    styles={selectPickerBaseStyles}
                    components={{
                        IndicatorSeparator: null,
                        DropdownIndicator: null,
                        SingleValue: EntityPillSelectWrapper,
                        MultiValue: MultiValueComponent,
                        Option: makeOptionComponent(labelKey),
                        LoadingMessage: makeMessage('Getting results…'),
                        NoOptionsMessage: makeMessage('Nothing was found.'),
                        ValueContainer: this.props.truncateMultiOptions
                            ? CustomValueContainer
                            : components.ValueContainer,
                    }}
                    // Multi?
                    isMulti={this.props.multi}
                    closeMenuOnSelect={!this.props.multi}
                    // Actual values
                    name={this.props.name}
                    label={this.props.label}
                    value={this.props.value}
                    loadOptions={this.props.loadOptions}
                    getOptionLabel={(option) => option[labelKey]}
                    getOptionValue={(option) => option[valueKey]}
                    autoBlur={this.props.autoBlur}
                    isClearable={this.props.clearable}
                    options={this.props.options}
                    onChange={this.props.onChange}
                    isDisabled={this.props.disabled}
                    backspaceRemovesValue={this.props.clearable}
                    // For styling purposes, we float of placeholder
                    onFocus={this.setFocus}
                    onBlur={this.clearFocus}
                    // Creatable properties
                    formatCreateLabel={this.props.formatCreateLabel}
                    onCreateOption={this.props.onCreateOption}
                    getNewOptionData={this.props.getNewOptionData}
                    createOptionPosition='first'
                    isValidNewOption={this.isValidNewOption}
                />
            </div>
        );
    }

    setFocus = () => {
        this.setState({hasFocus: true});
    };

    clearFocus = () => {
        this.setState({hasFocus: false});
    };

    renderRow = (rowText: string) => {
        return (
            <div styleName='styles.option-wrapper'>
                <div styleName='styles.option-label-wrapper'>
                    <div styleName='styles.option-label'>{rowText}</div>
                </div>
            </div>
        );
    };

    isValidNewOption = (inputValue: string) => {
        // One character just usually doesn't make sense
        // But we don't care about duplicates.
        return inputValue.length >= 2;
    };
}

const makeOptionComponent = (labelKey: string) => (props: OptionProps) => {
    // This is a hack to get around limitations in our old version of flow
    const option = props.data;

    return (
        <components.Option {...props}>
            <OptionRow>
                <div styleName='option-avatar'>
                    <Avatar
                        avatarUrl={option.avatarUrl}
                        initials={option.initials}
                        type={option.type}
                    />
                </div>
                <div styleName='styles.option-label'>{option[labelKey]}</div>
            </OptionRow>
        </components.Option>
    );
};

const makeMessage = (message: string) => () => (
    <OptionRow>
        <div styleName='styles.empty-label'>{message}</div>
    </OptionRow>
);

const OptionRow = (props: {children: React.Node}) => (
    <div styleName='styles.option-wrapper'>
        <div styleName='styles.option-label-wrapper'>{props.children}</div>
    </div>
);

const MultiValueComponent = (props: MultiValueProps) => {
    return (
        <EntityPillSelectWrapper
            data={props.data}
            {...props.removeProps}
            onRemove={props.isDisabled ? undefined : props.removeProps.onClick}
        />
    );
};

const CustomValueContainer = ({children, ...restProps}: ValueContainerProps) => {
    // State to control if all options are being shown, truncate by default
    const [showAll, setShowAll] = React.useState(false);

    // There isn't a good way to truncate only if the options break to the second line, it kinda has to be by number
    const TRUNCATE_AFTER_NUM = 3;

    // Convert children to an array
    const selectedOptions = React.Children.toArray(children);
    // Pop the last child, the input element
    const inputElementChild = selectedOptions.pop();

    let displayValues = children;

    // If the number of selected options exceeds the number to truncate at
    if (selectedOptions.length > TRUNCATE_AFTER_NUM) {
        // Slice off the options to show while the list is truncated
        const truncatedOptions = selectedOptions.slice(0, TRUNCATE_AFTER_NUM);

        displayValues = (
            <>
                {showAll ? selectedOptions : truncatedOptions}
                {inputElementChild}
                <Button
                    variant='text'
                    size='big'
                    onClick={() => {
                        setShowAll((prevState) => !prevState);
                    }}
                >
                    {showAll
                        ? 'See less'
                        : `+ ${selectedOptions.length - TRUNCATE_AFTER_NUM} more…`}
                </Button>
            </>
        );
    }

    return <components.ValueContainer {...restProps}>{displayValues}</components.ValueContainer>;
};
